import { openDialog } from '@patrianna/dialog-manager'
import {
  AccountHeartOutline,
  AccountMultiplePlusOutline,
  AccountOutline,
  CardBulletedOutline,
  ClipboardTextClockOutline,
  HeartOutline,
  FrequentlyAskedQuestions,
  GiftOutline,
  HomeOutline,
  Magnify,
  MedalOutline,
  NewBox,
  PokerChip,
  SlotMachineOutline,
  ArrowUpBoldBoxOutline,
  WalletOutline,
} from '@patrianna-icons/material-icons'
import { skipOtpClosedSelector, skipOtpMandatorySelector } from '@patrianna-payments/shared-store/skip-otp'
import { compose } from '@reduxjs/toolkit'

import { LiveGamesIcon } from 'components/LiveGamesIcon'
import { ProvidersIcon } from 'components/ProvidersIcon'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { sweepstakeEnabledSelector } from 'store/modules/appConfig/selectors'
import { getActiveCurrencyIdSelector } from 'store/modules/currencies/selectors'
import { openSnackbar } from 'store/modules/snackbar/actions'
import { getIsAdminUserSelector, isLoggedInSelector, showOtpPageSelector } from 'store/modules/user/selectors'

import type { ReactNode } from 'react'

const icons = {
  HomeOutline,
  SlotMachineOutline,
  CardBulletedOutline,
  AccountHeartOutline,
  NewBox,
  FrequentlyAskedQuestions,
  MedalOutline,
  AccountOutline,
  AccountMultiplePlusOutline,
  PokerChip,
  GiftOutline,
  Magnify,
  ClipboardTextClockOutline,
  HeartOutline,
  ArrowUpBoldBoxOutline,
  WalletOutline,
  ProvidersIcon,
  LiveGamesIcon,
}

type LinkObject = {
  icon: ReactNode
  text: string
  id: number
  link: string
  name: string
  flags?: Array<string>
  openDialog?: string
  openSnackbar?: string
  onClick?: () => void
  actionName?: string
}

type Links = Array<LinkObject>

type JsonLinks = Array<Omit<LinkObject, 'link'> & { link: string | { SC: string; GC: string } }>

const useBindEvents = (links: JsonLinks) => {
  const dispatch = useAppDispatch()
  const activeCurrency = useAppSelector(getActiveCurrencyIdSelector)

  return links.map((el) => {
    if (Object.prototype.hasOwnProperty.call(el, 'openDialog')) {
      return {
        ...el,
        onClick: () => {
          openDialog({ dialogName: el.openDialog })
        },
      }
    }

    if (Object.prototype.hasOwnProperty.call(el, 'openSnackbar')) {
      const message = el.openSnackbar

      return {
        ...el,
        onClick: () => {
          dispatch(openSnackbar({ message }))
        },
      }
    }

    if (Object.prototype.hasOwnProperty.call(el, 'actionName')) {
      return {
        ...el,
        onClick: () => {
          dispatch({ type: el.actionName })
        },
      }
    }

    if (typeof el?.link !== 'string' && el?.link?.SC) {
      return {
        ...el,
        link: el.link[activeCurrency],
      }
    }

    return el
  })
}

const bindIcons = (links: Links) => {
  return links.map((it) => ({
    ...it,
    icon: icons[it.icon as keyof typeof icons],
  }))
}

const setTags = (links: Links) => {
  return links.map((it) => ({
    ...it,
    name: it.text.toLowerCase().replace(/\s/g, '_'),
  }))
}

const useFilterLinksByFlags = (links: Links) => {
  const sweepstakeEnabled = useAppSelector(sweepstakeEnabledSelector)
  const isLoggedIn = useAppSelector(isLoggedInSelector)
  const isAdmin = useAppSelector(getIsAdminUserSelector)

  const flags = {
    sweepstakeEnabled,
    isLoggedIn,
    isAdmin,
  }

  return links
    .map((el) => {
      if (Object.prototype.hasOwnProperty.call(el, 'flags')) {
        return {
          ...el,
          show: el.flags.some((item) => flags[item as keyof typeof flags]),
        }
      }

      return {
        ...el,
        show: true,
      }
    })
    .filter((fl) => Boolean(fl.show))
}

export default compose<Links>(useBindEvents, bindIcons, useFilterLinksByFlags, setTags)

export const useShouldRedirectToOtp = () => {
  const showOtpPage = useAppSelector(showOtpPageSelector)
  const closedOtpPage = useAppSelector(skipOtpClosedSelector)
  const isOtpMandatory = useAppSelector(skipOtpMandatorySelector)
  const isClosed = closedOtpPage && !isOtpMandatory

  return showOtpPage && !isClosed
}
